/**
 * PROTOTYPE full module config definitions -- THROWAWAY.
 * Mirrors the production per-module config/rate drawers so every module's full
 * rate card comes over to the spread level. Defaults are representative placeholders.
 *
 * `common: true` fields surface in the drawer's pinned "Common" cluster (the knobs
 * people actually change); `desc` explains in plain words what the knob does.
 */
import type { ModuleKey } from './spreadSplitData';

export interface ConfigField { key: string; label: string; def: number; suffix?: '$' | '%' | 'x'; band?: string; common?: boolean; desc?: string; }
export interface ConfigGroup { title: string; fields: ConfigField[]; note?: string; }

const f = (key: string, label: string, def: number, suffix?: ConfigField['suffix'], band?: string): ConfigField => ({ key, label, def, suffix, band });
const fc = (key: string, label: string, def: number, suffix: ConfigField['suffix'], desc: string, band?: string): ConfigField => ({ key, label, def, suffix, band, common: true, desc });

// Shared blocks reused across the cost-model modules.
const NRE_RATES = (noun: string): ConfigGroup => ({
  title: `${noun} onboarding (NRE) rates`,
  fields: [
    f('nre.setupHours', 'Setup hours', 8), f('nre.perUnitHours', 'Hours / type', 2),
    f('nre.validationOverhead', 'Validation overhead', 25, '%'), f('nre.engineerRate', 'Engineer / hr', 100, '$'),
    f('nre.marginTarget', 'NRE margin', 75, '%'),
  ],
});
const DIFFICULTY = (noun: string): ConfigGroup => ({
  title: `${noun} format difficulty`,
  fields: [
    f('nre.difficultyMultipliers.api', 'API', 0.5, 'x'), f('nre.difficultyMultipliers.spreadsheet', 'Spreadsheet', 1, 'x'),
    f('nre.difficultyMultipliers.scannedPdf', 'Scanned PDF', 2, 'x'), f('nre.difficultyMultipliers.handwritten', 'Handwritten', 3, 'x'),
  ],
});
const DISCOUNT: ConfigGroup = { title: 'Discount', fields: [fc('durationDiscountPct', 'Duration discount', 0, '%', 'Discount on the recurring charge for longer contracts.')] };

export const MODULE_CONFIG_GROUPS: Record<ModuleKey, ConfigGroup[]> = {
  welding: [
    { title: 'Hardware', fields: [fc('monthlyRentalPerUnit', 'Monthly rental / unit', 500, '$', 'Monthly rent charged per welding unit.')] },
    { title: 'Software', fields: [fc('softwareFeePerUnit', 'Software fee / unit / mo', 416.67, '$', 'Monthly software licence per unit.')] },
    { title: 'Duration discount', fields: [
      fc('lessThan12Month', '≤ 12 mo', 0, '%', 'Discount for contracts of this length.', 'lessThan12Month'),
      fc('month13To24', '13-24 mo', 5, '%', 'Discount for contracts of this length.', 'month13To24'),
      fc('month25To36', '25-36 mo', 8, '%', 'Discount for contracts of this length.', 'month25To36'),
      fc('month37To60', '37-60 mo', 10, '%', 'Discount for contracts of this length.', 'month37To60'),
      fc('perpetual', 'Perpetual', 12, '%', 'Discount for a perpetual licence.', 'perpetual'),
    ] },
    { title: 'Internet & cloud', fields: [
      f('internetFee', 'Internet / mo', 50, '$'), f('awsCompute', 'Cloud compute / mo', 30, '$'), f('awsStorage', 'Cloud storage / mo', 20, '$'),
      f('liveModeInternetCloudMultiplier', 'Live mode', 1, 'x'), f('offlineModeInternetCloudMultiplier', 'Offline mode', 0.5, 'x'),
    ] },
    { title: 'Services', note: 'Charged once at project level (training/provisioning). These are rate overrides.', fields: [
      f('maintenanceAndSupport', 'Support / hr', 120, '$'), f('trainingRate', 'Training / hr', 120, '$'),
      f('trainingQuickHours', 'Quick hrs', 4), f('trainingMediumHours', 'Medium hrs', 8), f('trainingLongHours', 'Long hrs', 16),
      f('provisioningHourlyRate', 'Provisioning / hr', 100, '$'), f('onFieldSupportDailyRate', 'On-field / day', 800, '$'),
    ] },
    { title: 'Shipping', fields: [f('unitWeightKg', 'Unit weight kg', 25), f('shippingCostPerKg', 'Shipping / kg', 5, '$')] },
  ],
  mt: [
    { title: 'Per-pipe cost rates', fields: [
      f('hourlyRate', 'Exception labour / hr', 100, '$'), f('computeCostPerDoc', 'Compute / doc', 0.04, '$'), f('storagePerPipe', 'Storage / pipe', 0.04, '$'),
      f('qaOverheadPct', 'QA overhead', 10, '%'), fc('marginTarget', 'Per-pipe margin', 75, '%', 'Profit margin on the per-pipe tracking charge.'),
    ] },
    { title: 'Per-pipe bounds', fields: [f('floorPerPipe', 'Floor / pipe', 0.5, '$'), fc('ceilingPerPipe', 'Ceiling / pipe (0 = none)', 0, '$', 'Cap on the per-pipe charge.')] },
    { title: 'Per-pipe volume defaults', fields: [f('reportsPerPipe', 'Reports / pipe', 19), f('autoExtractRate', 'Auto-extract', 98, '%'), f('minutesPerException', 'Min / exception', 4)] },
    NRE_RATES('Mill'), DIFFICULTY('Mill'), DISCOUNT,
    { title: 'Add-on fees & floor', fields: [
      f('gasAddOnFee', 'Gas add-on', 2000, '$'), f('wireAddOnFee', 'Wire add-on', 2000, '$'),
      f('coatingMaterialsAddOnFee', 'Coating-materials', 3000, '$'), fc('moduleFloor', 'Module floor', 0, '$', 'Minimum total charge for this module on the spread.'),
    ] },
  ],
  ndt: [
    { title: 'Per-inspection cost model', fields: [
      f('reportsPerInspection', 'Reports / insp', 1), f('autoExtractRate', 'Auto-extract', 98, '%'), f('minutesPerException', 'Min / exception', 4),
      f('hourlyRate', 'Labour / hr', 100, '$'), f('computeCostPerDoc', 'Compute / doc', 0.04, '$'), f('aiComputeCostPerDoc', 'AI compute / doc', 0.2, '$'),
      f('storageCostPerInspection', 'Storage / insp', 0.05, '$'), f('qaOverheadPct', 'QA overhead', 10, '%'), fc('marginTarget', 'Margin', 75, '%', 'Profit margin on the per-inspection charge.'),
    ] },
    { title: 'Per-record bounds', fields: [f('floorPerInspection', 'Floor / insp', 0.5, '$'), fc('ceilingPerInspection', 'Ceiling / insp', 8, '$', 'Cap on the per-inspection charge.')] },
    NRE_RATES('Vendor'), DIFFICULTY('Vendor'),
    { title: 'Floor', fields: [fc('moduleFloor', 'Module floor', 20000, '$', 'Minimum total charge for this module on the spread.')] }, DISCOUNT,
  ],
  coating: [
    { title: 'Per-joint cost model', fields: [
      f('reportsPerJoint', 'Reports / joint', 1), f('autoExtractRate', 'Auto-extract', 98, '%'), f('minutesPerException', 'Min / exception', 4),
      f('hourlyRate', 'Labour / hr', 100, '$'), f('computeCostPerDoc', 'Compute / doc', 0.04, '$'), f('storageCostPerJoint', 'Storage / joint', 0.04, '$'),
      f('qaOverheadPct', 'QA overhead', 10, '%'), fc('marginTarget', 'Margin', 75, '%', 'Profit margin on the per-joint charge.'),
    ] },
    { title: 'Per-record bounds', fields: [f('floorPerJoint', 'Floor / joint', 0.5, '$'), fc('ceilingPerJoint', 'Ceiling / joint', 8, '$', 'Cap on the per-joint charge.')] },
    NRE_RATES('Contractor'), DIFFICULTY('Contractor'),
    { title: 'Floor', fields: [fc('moduleFloor', 'Module floor', 20000, '$', 'Minimum total charge for this module on the spread.')] }, DISCOUNT,
  ],
  platform: [
    { title: 'Platform fee', fields: [
      f('baseInfraMonthly', 'Base infra / mo', 500, '$'), f('perModuleInfraMonthly', 'Per-module infra / mo', 60, '$'),
      f('baseSupportHours', 'Support hours / mo', 40), f('supportHourlyRate', 'Support / hr', 40, '$'),
      fc('perUserMonthly', 'Per-user / mo', 40, '$', 'Monthly fee per platform user.'), fc('marginTarget', 'Margin', 75, '%', 'Profit margin on the platform fee.'),
    ] },
  ],
  retention: [
    { title: 'Retention annual fees', fields: [
      fc('coldArchiveAnnualFee', 'Cold archive / yr', 3000, '$', 'Annual fee for cold (archive) storage.'),
      fc('warmAccessAnnualFee', 'Warm access / yr', 15000, '$', 'Annual fee for warm (accessible) storage.'),
    ] },
  ],
};

export const MODULE_DEFAULTS: Record<ModuleKey, Record<string, number>> = Object.fromEntries(
  (Object.keys(MODULE_CONFIG_GROUPS) as ModuleKey[]).map((m) => [
    m, Object.fromEntries(MODULE_CONFIG_GROUPS[m].flatMap((g) => g.fields.map((fd) => [fd.key, fd.def]))),
  ]),
) as Record<ModuleKey, Record<string, number>>;

/** Which welding duration-discount band is active for a given duration. */
export function activeDiscountBand(months: number): string {
  if (months <= 12) return 'lessThan12Month';
  if (months <= 24) return 'month13To24';
  if (months <= 36) return 'month25To36';
  if (months <= 60) return 'month37To60';
  return 'perpetual';
}
